import { motion } from "framer-motion";

const MODI_LOGO =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_15aa463c-f5dd-46c0-8049-efe882557866/artifacts/2ks7hjk8_Modi%20Auto%20Group%20%28Black%29.png";

const CHAPTERS = [
  {
    no: "01",
    title: "A House Built on Trust",
    body: "For decades, Modi Auto Group has stood among India's most professionally run and trusted dealership houses — a name synonymous with integrity, discretion and an uncompromising standard of care.",
  },
  {
    no: "02",
    title: "From the Road to the Sea",
    body: "Modi Marine extends that legacy beyond the shoreline. The same obsessive attention to the ownership experience, now devoted to the world's most exclusive yachting marques.",
  },
  {
    no: "03",
    title: "By Invitation, By Appointment",
    body: "We serve a discerning few. Every acquisition is private, every consultation personal — because true luxury is never transacted, it is curated.",
  },
];

export default function Legacy() {
  return (
    <section id="legacy" className="relative bg-navy px-6 py-20 lg:px-12 lg:py-28" data-testid="legacy-section">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="mb-6 text-[10px] uppercase tracking-[0.45em] text-gold sm:text-xs"
              data-testid="legacy-overline"
            >
              01 — Our Legacy
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="max-w-xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-navy-ink sm:text-5xl lg:text-6xl"
              data-testid="legacy-heading"
            >
              A heritage of trust,
              <span className="italic text-gold-light"> now on water.</span>
            </motion.h2>

            <div className="mt-20 space-y-16">
              {CHAPTERS.map((c, i) => (
                <motion.div
                  key={c.no}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: i * 0.12 }}
                  className="grid gap-4 border-l border-gold/25 pl-8 sm:grid-cols-12 sm:gap-8"
                  data-testid={`legacy-chapter-${c.no}`}
                >
                  <span className="font-accent text-sm text-gold/70 sm:col-span-2">{c.no}</span>
                  <div className="sm:col-span-10">
                    <h3 className="font-serif text-2xl font-medium text-navy-ink sm:text-3xl">{c.title}</h3>
                    <p className="mt-4 max-w-lg text-sm font-light leading-relaxed text-mist sm:text-base">
                      {c.body}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="sticky top-28"
            >
              <div className="overflow-hidden border border-navy-ink/10 bg-navy px-8 py-12">
                <svg
                  viewBox="0 0 900 520"
                  fill="none"
                  className="mx-auto w-full max-w-xl"
                  role="img"
                  aria-label="Elegant line illustration of a superyacht at sea"
                  data-testid="legacy-image"
                >
                  <g stroke="#12233B" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M110 300 C300 282 520 278 700 276 C760 274 800 268 828 250" strokeWidth="1.75" />
                    <path d="M828 250 C810 305 745 352 630 372 C480 398 280 396 140 380" strokeWidth="1.75" />
                    <path d="M140 380 L110 300" strokeWidth="1.75" />
                    <path d="M200 282 L206 250 C340 242 480 242 610 250 L620 276" strokeWidth="1.5" />
                    <path d="M610 250 C640 254 660 264 674 278" strokeWidth="1.5" />
                    <path d="M228 264 L586 264" strokeWidth="1.25" strokeDasharray="20 12" />
                    <path d="M262 248 L268 220 C372 213 470 213 568 220 L576 244" strokeWidth="1.5" />
                    <path d="M568 220 C592 224 608 234 618 244" strokeWidth="1.5" />
                    <path d="M288 234 L552 234" strokeWidth="1.25" strokeDasharray="16 10" />
                    <path d="M352 218 L358 194 C420 188 468 188 518 194 L522 214" strokeWidth="1.5" />
                    <path d="M435 188 L435 150" strokeWidth="1.5" />
                    <path d="M435 150 L435 132" strokeWidth="1.25" />
                    <path d="M416 158 L454 158" strokeWidth="2.5" />
                    <circle cx="408" cy="180" r="7" strokeWidth="1.5" />
                    <circle cx="462" cy="180" r="7" strokeWidth="1.5" />
                    <path d="M706 262 L708 276 M746 258 L748 272 M786 252 L788 266" strokeWidth="1.25" />
                    <path d="M706 262 C750 254 790 248 822 240" strokeWidth="1.25" />
                    <circle cx="250" cy="342" r="4.5" strokeWidth="1.5" />
                    <circle cx="315" cy="344" r="4.5" strokeWidth="1.5" />
                    <circle cx="380" cy="345" r="4.5" strokeWidth="1.5" />
                    <circle cx="445" cy="344" r="4.5" strokeWidth="1.5" />
                    <circle cx="510" cy="342" r="4.5" strokeWidth="1.5" />
                  </g>
                  <g stroke="#8A6D1F" strokeLinecap="round">
                    <path d="M130 320 C340 302 560 298 800 264" strokeWidth="1.5" />
                    <path d="M828 250 L838 230" strokeWidth="1.5" />
                    <path d="M838 230 L862 236 L838 243" strokeWidth="1.5" />
                    <path d="M50 398 C80 394 105 394 132 398" strokeWidth="1.25" opacity="0.6" />
                    <path d="M690 402 C740 397 800 397 845 402" strokeWidth="1.25" opacity="0.6" />
                    <path d="M90 428 C200 420 320 420 430 428" strokeWidth="1.5" opacity="0.75" />
                    <path d="M520 434 C630 426 720 426 830 434" strokeWidth="1.5" opacity="0.75" />
                    <path d="M210 456 C310 450 430 450 530 456" strokeWidth="1.25" opacity="0.5" />
                    <path d="M600 458 C660 454 730 454 800 458" strokeWidth="1.25" opacity="0.5" />
                  </g>
                </svg>
                <p className="mt-6 text-center text-[9px] uppercase tracking-[0.5em] text-mist" data-testid="legacy-sketch-caption">
                  The Fleet Atelier
                </p>
              </div>
              <div className="mt-6 flex items-center justify-between border border-navy-ink/10 bg-navy-light/60 px-8 py-6 backdrop-blur-md">
                <img
                  src={MODI_LOGO}
                  alt="Modi Auto Group"
                  className="h-10 w-auto"
                  data-testid="legacy-modi-logo"
                />
                <span className="text-right text-[10px] uppercase leading-relaxed tracking-[0.3em] text-mist">
                  The Parent House
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
